import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { CognitoConfig } from './cognito';
import { AuthError, beginSignIn, completeSignIn, logoutUrl, refreshTokens } from './cognito';
import {
  RETURN_KEY,
  clearTokens,
  isExpired,
  loadTokens,
  refreshDelay,
  saveTokens,
  scheduleRefresh,
  storageGet,
  storageRemove,
  storageSet,
  type TokenSet,
} from './tokens';

export type AuthStatus = 'loading' | 'signedIn' | 'signedOut';

export interface Auth {
  status: AuthStatus;
  /** True in mock mode: no Cognito, everyone is signed in. */
  mock: boolean;
  getAccessToken(): Promise<string | undefined>;
  signIn(returnTo?: string): Promise<void>;
  /** Handles `/auth/callback`. Returns the path to go to next. */
  finishSignIn(search: URLSearchParams): Promise<string>;
  signOut(): void;
  /** Called when the API answers 401: forget tokens and show sign-in. */
  expire(): void;
}

export const AuthContext = createContext<Auth | null>(null);

export function useAuth(): Auth {
  const a = useContext(AuthContext);
  if (!a) throw new Error('useAuth needs an AuthProvider');
  return a;
}

export function mockAuth(status: AuthStatus = 'signedIn'): Auth {
  return {
    status,
    mock: true,
    getAccessToken: async () => 'mock-token',
    signIn: async () => {},
    finishSignIn: async () => '/',
    signOut: () => {},
    expire: () => {},
  };
}

interface Props {
  config: CognitoConfig;
  mock?: boolean;
  children: ReactNode;
  /** For tests. */
  fetchImpl?: typeof fetch;
  navigate?: (url: string) => void;
}

export function AuthProvider({ config, mock = false, children, fetchImpl, navigate }: Props) {
  const tokensRef = useRef<TokenSet | undefined>(undefined);
  const refreshing = useRef<Promise<TokenSet | undefined> | null>(null);
  const [tokens, setTokens] = useState<TokenSet | undefined>(undefined);
  const [status, setStatus] = useState<AuthStatus>(mock ? 'signedIn' : 'loading');
  const go = useCallback((url: string) => (navigate ? navigate(url) : window.location.assign(url)), [navigate]);
  const doFetch = fetchImpl ?? fetch;

  const apply = useCallback((t: TokenSet | undefined) => {
    tokensRef.current = t;
    setTokens(t);
    if (t) saveTokens(t);
    else clearTokens();
    setStatus(t ? 'signedIn' : 'signedOut');
  }, []);

  const refresh = useCallback(async (): Promise<TokenSet | undefined> => {
    const current = tokensRef.current;
    if (!current?.refreshToken) {
      apply(undefined);
      return undefined;
    }
    refreshing.current ??= refreshTokens(config, current, doFetch)
      .then((t) => {
        apply(t);
        return t;
      })
      .catch((err: unknown) => {
        // offline or a hiccup at Cognito: keep the session and try again on the next request or wake-up
        if (err instanceof AuthError && err.offline) {
          apply(current); // signed in with the stored tokens; the next request or wake-up refreshes again
          return undefined;
        }
        apply(undefined);
        return undefined;
      })
      .finally(() => {
        refreshing.current = null;
      });
    return refreshing.current;
  }, [apply, config, doFetch]);

  // Restore the session on load.
  useEffect(() => {
    if (mock) return;
    const stored = loadTokens();
    if (!stored) {
      setStatus('signedOut');
      return;
    }
    tokensRef.current = stored;
    if (isExpired(stored, Date.now())) void refresh();
    else apply(stored);
  }, [mock, apply, refresh]);

  // Refresh shortly before expiry.
  useEffect(() => {
    if (mock || !tokens) return;
    return scheduleRefresh(tokens, () => void refresh());
  }, [mock, tokens, refresh]);

  // Timers stop while a laptop sleeps or a phone parks the tab: refresh when the page comes back.
  useEffect(() => {
    if (mock) return;
    const wake = () => {
      const t = tokensRef.current;
      if (document.visibilityState === 'visible' && t && refreshDelay(t, Date.now()) === 0) void refresh();
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    window.addEventListener('focus', wake);
    return () => {
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      window.removeEventListener('focus', wake);
    };
  }, [mock, refresh]);

  const value = useMemo<Auth>(
    () => ({
      status,
      mock,
      async getAccessToken() {
        if (mock) return 'mock-token';
        const t = tokensRef.current;
        if (!t) return undefined;
        if (refreshDelay(t, Date.now()) === 0) return (await refresh())?.accessToken;
        return t.accessToken;
      },
      async signIn(returnTo) {
        if (mock) return;
        storageSet(RETURN_KEY, returnTo ?? window.location.pathname + window.location.search);
        go(await beginSignIn(config));
      },
      async finishSignIn(search) {
        if (mock) return '/';
        const t = await completeSignIn(
          config,
          { code: search.get('code'), state: search.get('state'), error: search.get('error') },
          doFetch,
        );
        apply(t);
        const back = storageGet(RETURN_KEY);
        storageRemove(RETURN_KEY);
        return back && back.startsWith('/') && !back.startsWith('/auth/') ? back : '/';
      },
      signOut() {
        if (mock) return;
        apply(undefined);
        go(logoutUrl(config));
      },
      expire() {
        if (!mock) apply(undefined);
      },
    }),
    [status, mock, refresh, go, config, doFetch, apply],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
